'use client'

import React, { memo, useCallback, useState } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { LinkButton } from '../parts/LinkButton'
import { useCLickEvent } from '@/hooks/use-tracker'
import { useFromParams } from '@/app/hooks/use-from-params'
import { usePathname } from 'next/navigation'

const navItemPaddingX = 'px-4 lg:px-[4px] lg:px-[6px] xl:px-[10px]'
const navParentItemPaddingX = 'px-4 lg:pl-[4px] lg:pr-[4px] lg:pl-[10px]'
const navItemText = 'text-sm lg:text-xs xl:text-sm text-textblack font-bold'
const navChildrenWrapper =
  'bg-white lg:flex lg:flex-col [&>*+*]:border-t [&>*+*]:border-gray lg:[&>*+*]:border-none'

const Logo = (props: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="90"
      height="21"
      viewBox="0 0 90 21"
      fill="none"
      className={props.className}
    >
      <title>LAWGUE</title>
      <desc>LAWGUE</desc>
      <path
        d="M8.50307 20.188H0.776373C0.674134 20.1886 0.572792 20.1696 0.478219 20.1323C0.383645 20.095 0.297722 20.04 0.225427 19.9705C0.153132 19.901 0.0959042 19.8184 0.057059 19.7275C0.0182139 19.6366 -0.00147584 19.5392 -0.000869073 19.4409V1.55471C-0.00644072 1.45312 0.00950665 1.3515 0.0460065 1.256C0.0825064 1.16051 0.138799 1.07313 0.211472 0.999168C0.284145 0.925205 0.371685 0.866196 0.468788 0.825717C0.56589 0.785238 0.670532 0.764132 0.776373 0.763678H2.28514C2.39442 0.759335 2.50345 0.776823 2.60534 0.81504C2.70723 0.853256 2.79977 0.911369 2.87711 0.985708C2.95445 1.06005 3.0149 1.14899 3.05466 1.24693C3.09442 1.34487 3.11262 1.44967 3.1081 1.55471V16.6283C3.09513 16.7314 3.10594 16.836 3.13975 16.9346C3.17355 17.0333 3.22954 17.1235 3.30372 17.199C3.37791 17.2745 3.46847 17.3334 3.56899 17.3715C3.66951 17.4096 3.77751 17.4259 3.88534 17.4194H8.50307C9.05171 17.4194 9.28031 17.683 9.28031 18.2104V19.4409C9.28485 19.5402 9.26785 19.6393 9.2304 19.7319C9.19294 19.8245 9.13585 19.9086 9.06275 19.9789C8.98965 20.0491 8.90215 20.104 8.80579 20.14C8.70944 20.176 8.60635 20.1923 8.50307 20.188V20.188ZM24.9166 20.188H23.4078C23.1726 20.2004 22.9414 20.1251 22.7626 19.9777C22.5837 19.8303 22.4709 19.6223 22.4477 19.3969L21.8991 16.7602C21.8612 16.5291 21.7394 16.3185 21.5552 16.1656C21.3711 16.0127 21.1365 15.9275 20.8932 15.9252H18.1957C17.9709 15.9533 17.7613 16.0493 17.5969 16.1994C17.4326 16.3495 17.3221 16.5458 17.2813 16.7602L16.7327 19.4409C16.6988 19.6583 16.5814 19.8555 16.4037 19.9938C16.226 20.1321 16.0008 20.2014 15.7726 20.188H14.3095C13.8066 20.188 13.6237 19.7925 13.6694 19.3969L17.2813 1.51077C17.3023 1.29621 17.4096 1.09801 17.5804 0.958438C17.7513 0.81887 17.9721 0.748981 18.1957 0.763678H20.9389C21.1797 0.748254 21.4176 0.821394 21.6044 0.968277C21.7911 1.11516 21.9128 1.3248 21.9448 1.55471L25.6938 19.3969C25.7112 19.5008 25.7036 19.6071 25.6714 19.7077C25.6392 19.8082 25.5834 19.9003 25.5082 19.9768C25.4331 20.0532 25.3406 20.1121 25.238 20.1487C25.1354 20.1853 25.0254 20.1988 24.9166 20.188ZM19.7502 5.3341C19.7045 5.15832 19.4759 5.07042 19.4302 5.37805L18.1043 12.4974C18.0128 12.9808 18.3786 13.2005 18.7901 13.2005H20.3903C20.8932 13.2005 21.1675 12.9368 21.0761 12.4534L19.7502 5.3341ZM43.1589 19.3969C43.1258 19.6065 43.0151 19.7976 42.847 19.9349C42.679 20.0721 42.465 20.1464 42.2445 20.144H40.9186C40.6956 20.1377 40.4809 20.0611 40.3074 19.9262C40.134 19.7912 40.0114 19.6053 39.9585 19.3969L37.6268 7.97089C37.581 7.57537 37.3982 7.57537 37.3067 7.97089L35.2036 19.3969C35.1614 19.6188 35.0353 19.8178 34.8496 19.9558C34.6639 20.0937 34.4318 20.1607 34.1978 20.144H32.9176C32.6971 20.1464 32.4831 20.0721 32.315 19.9349C32.147 19.7976 32.0363 19.6065 32.0032 19.3969L28.4827 1.55471C28.4654 1.46405 28.4689 1.37084 28.493 1.28161C28.5171 1.19238 28.5611 1.10928 28.6221 1.03812C28.683 0.966961 28.7594 0.909465 28.8459 0.869652C28.9324 0.829838 29.0269 0.808669 29.1228 0.807625H30.5402C30.7738 0.801477 31.0014 0.879036 31.1789 1.02523C31.3563 1.17142 31.4709 1.37581 31.5003 1.59866L33.5577 12.5853C33.6491 12.8929 33.7863 12.8929 33.832 12.5413L35.9351 1.59866C35.9906 1.39543 36.1068 1.21224 36.2692 1.07168C36.4317 0.931125 36.6334 0.839334 36.8495 0.807625H37.9925C38.2168 0.831185 38.4285 0.91931 38.5998 1.06046C38.7711 1.20161 38.8941 1.3892 38.9526 1.59866L41.2387 12.5413C41.3301 12.8489 41.513 12.8489 41.5587 12.5413L43.3418 1.59866C43.3815 1.37215 43.5053 1.16715 43.6903 1.0217C43.8752 0.876236 44.1088 0.800178 44.3476 0.807625H45.5821C46.1307 0.807625 46.3593 1.1592 46.2679 1.64261L43.1589 19.3969ZM60.3039 20.188H58.7952C58.1094 20.188 58.1551 19.6606 57.9265 19.6606C57.789 19.6826 57.6615 19.7439 57.5607 19.8364C56.8545 20.275 56.0231 20.4901 55.1833 20.4517C54.3312 20.4803 53.4874 20.282 52.7452 19.8787C52.0029 19.4755 51.391 18.8829 50.977 18.1664C50.2912 16.892 50.1084 15.7054 50.1084 12.9808V7.66326C50.1084 4.49912 50.2912 3.44441 51.2514 2.3018C52.2115 1.1592 53.7202 0.5 55.549 0.5C59.2524 0.5 61.0355 3.04889 61.0355 6.38882C61.0437 6.48894 61.0293 6.5896 60.9931 6.68385C60.957 6.7781 60.9 6.86368 60.8261 6.93471C60.7522 7.00573 60.6632 7.0605 60.5651 7.09524C60.4671 7.12997 60.3624 7.14385 60.2582 7.1359H58.7494C58.6424 7.13946 58.5359 7.12107 58.4368 7.08196C58.3378 7.04286 58.2485 6.98393 58.175 6.9091C58.1015 6.83427 58.0453 6.74526 58.0104 6.64797C57.9754 6.55069 57.9624 6.44736 57.9722 6.34487C57.8808 4.36728 57.0578 3.31257 55.549 3.31257C53.8574 3.31257 53.263 4.32334 53.263 7.1359V13.0247C53.263 16.4965 53.9031 17.683 55.7319 17.683C57.3321 17.683 58.0179 16.5404 58.0179 13.9476C58.0179 13.4202 57.835 13.0247 57.195 13.0247H56.2806C56.1735 13.0283 56.067 13.0099 55.9679 12.9708C55.8689 12.9317 55.7796 12.8727 55.7061 12.7979C55.6326 12.7231 55.5765 12.6341 55.5415 12.5368C55.5065 12.4395 55.4935 12.3362 55.5033 12.2337V11.3548C55.4896 11.2494 55.4995 11.1424 55.5324 11.0411C55.5654 10.9397 55.6205 10.8463 55.6942 10.7672C55.7678 10.688 55.8583 10.625 55.9594 10.5823C56.0606 10.5397 56.1701 10.5183 56.2806 10.5198H60.3496C60.5692 10.5293 60.7778 10.6145 60.9373 10.7597C61.0968 10.905 61.1966 11.1006 61.2183 11.3108V19.3969C61.1855 19.6105 61.0763 19.8063 60.9093 19.9507C60.7424 20.0951 60.5283 20.179 60.3039 20.188V20.188ZM75.8945 18.7817C74.9801 19.9243 73.5628 20.5396 71.7339 20.4956C69.9051 20.4517 68.3964 19.8804 67.5277 18.7817C66.659 17.5951 66.3847 16.4086 66.3847 13.9915V1.55471C66.3809 1.35137 66.4602 1.15469 66.6055 1.00683C66.7508 0.858975 66.9505 0.77169 67.1619 0.763678H68.6707C68.7809 0.755801 68.8915 0.770859 68.9951 0.807827C69.0986 0.844794 69.1927 0.9028 69.2708 0.977887C69.3489 1.05297 69.4093 1.14337 69.4477 1.24292C69.4862 1.34247 69.5019 1.44882 69.4937 1.55471V14.431C69.4937 16.7602 70.1337 17.683 71.7339 17.683C73.3342 17.683 73.9285 16.7602 73.9285 14.431V1.55471C73.9203 1.44882 73.936 1.34247 73.9744 1.24292C74.0129 1.14337 74.0733 1.05297 74.1514 0.977887C74.2295 0.9028 74.3235 0.844794 74.4271 0.807827C74.5307 0.770859 74.6413 0.755801 74.7515 0.763678H76.2145C76.3264 0.764139 76.4369 0.786386 76.5396 0.829074C76.6422 0.871763 76.7348 0.934007 76.8117 1.01205C76.8886 1.09009 76.9483 1.18231 76.9871 1.28313C77.0259 1.38395 77.043 1.49129 77.0375 1.59866V13.9476C77.0375 16.4086 76.7632 17.683 75.8945 18.7817ZM91.4393 19.3969C91.4388 19.4987 91.4169 19.5993 91.3748 19.6926C91.3327 19.7859 91.2713 19.8701 91.1943 19.9399C91.1174 20.0098 91.0265 20.0639 90.9271 20.099C90.8278 20.1341 90.722 20.1494 90.6163 20.144H83.1183C82.9067 20.1476 82.7021 20.0715 82.5483 19.9318C82.3944 19.7922 82.3036 19.6002 82.2953 19.3969V1.55471C82.3249 1.35499 82.4211 1.1699 82.5695 1.02725C82.7179 0.884607 82.9105 0.792114 83.1183 0.763678H90.6163C91.165 0.763678 91.3936 1.02736 91.3936 1.55471V2.78521C91.4061 2.88621 91.3946 2.98863 91.3599 3.08474C91.3253 3.18085 91.2684 3.26815 91.1936 3.34007C91.1188 3.41199 91.0279 3.46666 90.928 3.49996C90.828 3.53326 90.7214 3.54431 90.6163 3.5323H86.1815C86.0651 3.5309 85.9497 3.5534 85.843 3.5983C85.7363 3.64319 85.6409 3.70945 85.563 3.79264C85.4851 3.87584 85.4265 3.97405 85.3913 4.08073C85.356 4.1874 85.3449 4.30008 85.3585 4.41123V7.83905C85.3548 7.94191 85.374 8.04435 85.4147 8.13956C85.4553 8.23477 85.5166 8.32056 85.5945 8.39123C85.6723 8.4619 85.765 8.51583 85.8662 8.54944C85.9674 8.58304 86.0749 8.59555 86.1815 8.58613H89.9305C90.0384 8.57958 90.1464 8.59592 90.2469 8.63399C90.3474 8.67207 90.438 8.73095 90.5122 8.80645C90.5863 8.88195 90.6423 8.97222 90.6761 9.07086C90.71 9.16949 90.7208 9.27407 90.7078 9.37717V10.6077C90.702 10.8041 90.6183 10.9909 90.4738 11.1298C90.3292 11.2687 90.1349 11.3492 89.9305 11.3548H86.1358C86.0276 11.3604 85.9217 11.3864 85.8241 11.4315C85.7265 11.4765 85.6391 11.5396 85.5669 11.6172C85.4947 11.6948 85.4391 11.7853 85.4033 11.8835C85.3676 11.9818 85.3524 12.0858 85.3585 12.1897V16.5844C85.3456 16.6875 85.3564 16.7921 85.3902 16.8907C85.424 16.9893 85.48 17.0796 85.5542 17.1551C85.6283 17.2306 85.7189 17.2895 85.8194 17.3276C85.9199 17.3656 86.028 17.382 86.1358 17.3754H90.5706C90.7893 17.3963 90.9928 17.4922 91.1439 17.6455C91.2951 17.7989 91.3837 17.9994 91.3936 18.2104V19.3969H91.4393Z"
        fill="#424242"
      />
    </svg>
  )
}

const LinkBorder = memo(({ isCurrent }: { isCurrent?: boolean }) => {
  return (
    <div
      className={clsx(
        'hidden lg:block absolute pointer-events-none bottom-0 left-0 w-full bg-primary h-[2px] scale-0 lg:group-hover/item:scale-100 transition-transform duration-200 ease-in-out',
        !!isCurrent && 'scale-100'
      )}
      aria-hidden={true}
    />
  )
})

LinkBorder.displayName = 'LinkBorder'

const HeaderLinkItem = memo(
  ({
    href,
    label,
    isCurrent,
    onCloseMenu,
    className
  }: {
    href: string
    label: string
    isCurrent: boolean
    onCloseMenu: () => void
    className?: string
  }) => {
    return (
      <Link
        href={href}
        className={clsx(
          'relative flex items-center border-t border-solid lg:border-t-0 border-gray py-4 lg:py-0 h-full group/item',
          navItemPaddingX,
          navItemText,
          className
        )}
        onClick={onCloseMenu}
      >
        {label}
        <LinkBorder isCurrent={isCurrent} />
      </Link>
    )
  }
)

HeaderLinkItem.displayName = 'HeaderItem'

const triangle =
  'relative content:[""] before:absolute before:w-2 before:h-2 before:border-t before:border-r'

const ParentItem = memo(
  ({
    label,
    isCurrent,
    isOpened,
    onToggle
  }: {
    label: string
    isCurrent: boolean
    isOpened: boolean
    onToggle: () => void
  }) => {
    return (
      <div
        className={clsx(
          `relative cursor-pointer border-t border-solid lg:border-t-0 border-gray py-4 lg:py-0 flex items-center justify-between h-full`,
          navParentItemPaddingX,
          navItemText
        )}
      >
        {label}
        <button
          className={'absolute lg:hidden top-0 left-0 w-full h-full opacity-0'}
          type={'button'}
          onClick={() => {
            onToggle()
          }}
        >
          {isOpened ? '閉じる' : '開く'}
        </button>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden={true}
          className={clsx(
            'transition-transform duration-200 ease-in-out',
            'lg:rotate-0 lg:group-hover/item:rotate-180 lg:group-focus-within/item:rotate-180 pointer-events-none',
            isOpened ? 'rotate-180' : 'rotate-0'
          )}
        >
          <path
            d="M5.84375 9.59323L11.5008 15.2502L17.1577 9.59323L16.4508 8.88623L11.5008 13.8362L6.55075 8.88623L5.84375 9.59323Z"
            fill="#424242"
          />
        </svg>
        <LinkBorder isCurrent={isCurrent} />
      </div>
    )
  }
)

ParentItem.displayName = 'ParentItem'

const ChildItem = memo(
  ({
    href,
    label,
    subLabel,
    img,
    onCloseMenu
  }: {
    href: string
    label: string
    subLabel?: string
    img?: string
    onCloseMenu: () => void
  }) => {
    return (
      <Link
        href={href}
        className={clsx(
          'relative flex items-start bg-lawguebase lg:bg-white px-6 py-4  lg:pr-1 transition lg:hover:bg-[#4E7AC714] lg:hover:bg-opacity-20',
          navItemText,
          img ? 'lg:pl-6 lg:py-5' : 'lg:pl-1 lg:py-3'
        )}
        onClick={(e) => {
          e.currentTarget.blur()
          onCloseMenu()
        }}
      >
        {img && <img src={img} alt="" className="w-6 hidden lg:block" />}
        <span className="ml-4">
          <span>{label}</span>
          {subLabel && (
            <span className="text-xxs font-normal hidden lg:block">
              {subLabel}
            </span>
          )}
        </span>
      </Link>
    )
  }
)

ChildItem.displayName = 'ChildItem'

const LevelMenu = memo(
  ({
    parentLabel,
    children,
    isCurrent
  }: {
    parentLabel: string
    children: React.ReactNode
    isCurrent: boolean
  }) => {
    const [opened, setOpened] = useState(true)
    const handleToggle = useCallback(() => {
      setOpened((s) => !s)
    }, [])
    // サブのメニュークリックしたらホバーアニメーションを無効化する
    const [isClear, setIsClear] = useState(false)
    return (
      <div
        className={`relative group/item h-full`}
        tabIndex={0}
        role={'button'}
        onMouseLeave={(e) => {
          e.currentTarget.blur()
        }}
      >
        <ParentItem
          label={parentLabel}
          isCurrent={isCurrent}
          isOpened={opened}
          onToggle={handleToggle}
        />
        <div
          className={clsx(
            `lg:absolute lg:top-full lg:invisible lg:shadow-[0_4px_16px_rgba(0,0,0,.1)]`,
            'transition-[grid-template-rows] lg:transition-[visibility,opacity] lg:duration-300 lg:opacity-0  lg:group-focus-within/item:visible lg:group-focus-within/item:opacity-100',
            'lg:group-hover/item:visible lg:group-hover/item:opacity-100',
            'grid lg:block',
            isClear && 'lg:pointer-events-none',
            opened ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div
            className={clsx('overflow-hidden lg:overflow-visible')}
            onClick={() => {
              setIsClear(true)
              setTimeout(() => {
                setIsClear(false)
              }, 500)
            }}
          >
            {children}
          </div>
        </div>
      </div>
    )
  }
)

LevelMenu.displayName = 'LevelMenu'

export const Header = memo((props: { path: string }) => {
  const [menuOpened, setMenuOpened] = useState(false)

  const handleMenuToggle = useCallback(() => {
    setMenuOpened((s) => !s)
  }, [])

  const closeMenu = useCallback(() => {
    setMenuOpened(false)
  }, [])

  const { pushGa4ClickEvent } = useCLickEvent()

  const pathname = usePathname()
  const params = useFromParams()

  return (
    <>
      <div className="z-50 sticky top-0 w-full left-0 lg:hidden items-center flex justify-center py-[1.125rem] bg-white border-b border-solid border-gray text-center">
        <Link href="/">
          <Logo />
        </Link>
      </div>

      <header
        className={`sticky w-full bottom-0 lg:bottom-auto lg:top-0 left-0 z-50 items-center justify-center lg:flex bg-white lg:border-b lg:border-solid lg:border-gray lg:h-[64px] lg:px-5 xl:px-0`}
      >
        <div className="lg:w-[1200px] flex items-center lg:h-[64px]">
          <Link
            href="/"
            className={clsx(
              'hidden h-full lg:flex items-center',
              navItemPaddingX,
              'relative md:ml-[-4px] lg:ml-[-10px] lg:mr-[10px] group/item'
            )}
          >
            <div>
              <Logo className={'w-[85px] lg:w-auto'} />
            </div>
            {/*<img*/}
            {/*  src="/images/img_logo.svg"*/}
            {/*  alt="LAWGUE"*/}
            {/*  className={'w-[85px] lg:w-auto'}*/}
            {/*/>*/}
            <LinkBorder isCurrent={false} />
          </Link>
          <div
            className={clsx(
              'bg-lawguebg lg:bg-white fixed overflow-auto lg:overflow-visible lg:static h-[calc(100%-58px)] lg:h-full w-full lg:w-auto top-0 lg:visible lg:opacity-100',
              'transition-[visibility,opacity] duration-300',
              menuOpened ? 'visible opacity-100' : 'invisible opacity-0'
            )}
          >
            <nav className="lg:flex lg:flex-row lg:h-full items-center">
              <LevelMenu
                parentLabel={'ソリューション'}
                isCurrent={/^\/contract|^\/regulation|^\/law|^\/translation/.test(
                  props.path
                )}
              >
                <div className={clsx(navChildrenWrapper, `lg:w-[15.31rem]`)}>
                  <ChildItem
                    href={'/contract'}
                    label={'契約ナレッジマネジメント'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/regulation'}
                    label={'規程・マニュアル管理'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/law'}
                    label={'法令改正対応'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/translation'}
                    label={'英文契約書レビュー'}
                    onCloseMenu={closeMenu}
                  />
                  <Link
                    href="https://d1.lawgue.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="relative flex items-start bg-lawguebase lg:bg-white px-6 py-4  lg:pr-1 transition lg:hover:bg-[#4E7AC714] lg:hover:bg-opacity-20 text-sm lg:text-xs xl:text-sm text-textblack font-bold lg:pl-1 lg:py-3"
                  >
                    <span className="ml-4">
                      <span>省庁・自治体向け</span>
                    </span>
                    <img
                      src="/images/img_icon_externallink.svg"
                      alt=""
                      width={16}
                      height={17}
                      className="ml-2 w-5"
                    />
                  </Link>
                </div>
              </LevelMenu>
              <LevelMenu
                parentLabel={'製品紹介'}
                isCurrent={/^\/functions|^\/security/.test(props.path)}
              >
                <div className={clsx(navChildrenWrapper, `lg:w-[157px]`)}>
                  <ChildItem
                    href={'/functions'}
                    label={'機能'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/security'}
                    label={'セキュリティ'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/flow'}
                    label={'料金・導入の流れ'}
                    onCloseMenu={closeMenu}
                  />
                </div>
              </LevelMenu>
              <HeaderLinkItem
                href={'/demo'}
                label={'ライブデモ'}
                isCurrent={/^\/demo/.test(props.path)}
                onCloseMenu={closeMenu}
                className="hidden lg:flex"
              />
              <HeaderLinkItem
                href={'/voice'}
                label={'導入事例'}
                isCurrent={/^\/voice/.test(props.path)}
                onCloseMenu={closeMenu}
              />
              <LevelMenu
                parentLabel={'お役立ち情報'}
                isCurrent={/^\/service|^\/seminar|^\/column|^\/news/.test(
                  props.path
                )}
              >
                <div className={clsx(navChildrenWrapper, `lg:w-[157px]`)}>
                  <ChildItem
                    href={'/service'}
                    label={'お役立ち資料'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/seminar'}
                    label={'セミナー'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/column'}
                    label={'コラム'}
                    onCloseMenu={closeMenu}
                  />
                  <ChildItem
                    href={'/news'}
                    label={'お知らせ'}
                    onCloseMenu={closeMenu}
                  />
                </div>
              </LevelMenu>
              <HeaderLinkItem
                href={'/partnership'}
                label={'パートナーシップ'}
                isCurrent={/^\/partnership/.test(props.path)}
                onCloseMenu={closeMenu}
              />
            </nav>
            <div className="lg:hidden border-t border-solid border-gray px-8 pt-4 pb-4">
              <LinkButton
                href="/contact"
                size="small"
                bg="hidden"
                full="full"
                label="お問い合わせ"
                onClick={() => {
                  pushGa4ClickEvent({
                    eventCategory: 'contact',
                    eventUi: 'header'
                  })
                }}
              />
            </div>
          </div>
          <div
            className={`flex items-center ml-auto mr-0 py-2 lg:py-0 w-full lg:w-auto px-4 lg:px-0 justify-between z-20 bg-white fixed lg:static bottom-0 border-t lg:border-t-0 border-solid border-gray`}
          >
            <div className="hidden lg:block lg:mr-2">
              <LinkButton
                href={`/contact/${pathname === '/demo/' ? params : ''}`}
                size="small"
                bg="hidden"
                label="お問い合わせ"
                onClick={() => {
                  pushGa4ClickEvent({
                    eventCategory: 'contact',
                    eventUi: 'header'
                  })
                }}
              />
            </div>

            <Link
              href="/"
              className="block mr-auto lg:hidden"
              onClick={closeMenu}
            >
              <img
                src="/images/img_icon_home.svg"
                alt="LAWGUE トップページ"
                className="w-[32px]"
                width={32}
                height={36}
              />
            </Link>
            <div className={`lg:ml-0 mx-auto`}>
              <LinkButton
                href={`/request/${pathname === '/demo/' ? params : ''}`}
                size="small"
                bg="visible"
                label="資料請求"
                onClick={() => {
                  pushGa4ClickEvent({
                    eventCategory: 'contact',
                    eventUi: 'header'
                  })
                  closeMenu()
                }}
              />
            </div>
            <div className="lg:ml-3 lg:border-l lg:border-solid lg:border-centerborder hidden lg:block lg:h-10">
              <Link
                href="https://app.lawgue.com/login"
                className={`lg:ml-1 base_button linkbutton__small_login button__hidden button__none transition hover:text-secondary hover:border-secondary`}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                <img
                  src="/images/icons/icon-login.svg"
                  alt="ログイン"
                  width={28}
                  height={28}
                />
              </Link>
            </div>
            <button
              type="button"
              className={`w-15 ml-auto lg:hidden`}
              onClick={handleMenuToggle}
              aria-label="menuopen"
            >
              <span className="block w-full">
                <span
                  className={clsx(
                    `bg-black block w-8 h-0.5`,
                    menuOpened && 'transform rotate-45'
                  )}
                ></span>
                <span
                  className={clsx(
                    `bg-black w-8 h-0.5 mt-2`,
                    menuOpened ? 'hidden' : 'block'
                  )}
                ></span>
                <span
                  className={clsx(
                    `bg-black block w-8 h-0.5`,
                    menuOpened ? 'transform -rotate-45 -mt-0.5' : 'mt-2'
                  )}
                ></span>
              </span>
            </button>
          </div>
        </div>
      </header>
    </>
  )
})

Header.displayName = 'Header'
